// @ts-check

/**
 * @file Resolves Expo public environment values across web and native bundles.
 */

import Constants from "expo-constants"

/**
 * @typedef {"EXPO_PUBLIC_ADB_IP" | "EXPO_PUBLIC_BACKEND_HOST" | "EXPO_PUBLIC_BACKEND_PORT" | "EXPO_PUBLIC_BACKEND_PROTOCOL" | "EXPO_PUBLIC_SYSTEM_TEST" | "EXPO_PUBLIC_SYSTEM_TEST_CLIENT_WS_PORT" | "EXPO_PUBLIC_SYSTEM_TEST_HOST" | "EXPO_PUBLIC_SYSTEM_TEST_PROJECT_SYNC" | "EXPO_PUBLIC_SYSTEM_TEST_QUERY_LOGGING" | "EXPO_PUBLIC_SYSTEM_TEST_SCOUNDREL_PORT" | "EXPO_PUBLIC_VELOCIOUS_ENV"} ExpoPublicEnvName
 */

/**
 * Reads public env values embedded in Expo native config extras.
 * @returns {Record<string, unknown>} Embedded public env values.
 */
function expoPublicEnvFromConstants() {
  const extra = Constants.expoConfig?.extra

  if (!extra || typeof extra !== "object" || Array.isArray(extra)) return {}

  const expoPublicEnv = extra.expoPublicEnv

  if (!expoPublicEnv || typeof expoPublicEnv !== "object" || Array.isArray(expoPublicEnv)) return {}

  return /** @type {Record<string, unknown>} */ (expoPublicEnv)
}

/**
 * Reads a public env value from the runtime process shim when present.
 * @param {ExpoPublicEnvName} name Public Expo env name.
 * @returns {string | undefined} Runtime process env value.
 */
function processEnvValue(name) {
  if (typeof process === "undefined" || !process.env) return undefined

  let value

  switch (name) {
    case "EXPO_PUBLIC_ADB_IP":
      value = process.env.EXPO_PUBLIC_ADB_IP
      break
    case "EXPO_PUBLIC_BACKEND_HOST":
      value = process.env.EXPO_PUBLIC_BACKEND_HOST
      break
    case "EXPO_PUBLIC_BACKEND_PORT":
      value = process.env.EXPO_PUBLIC_BACKEND_PORT
      break
    case "EXPO_PUBLIC_BACKEND_PROTOCOL":
      value = process.env.EXPO_PUBLIC_BACKEND_PROTOCOL
      break
    case "EXPO_PUBLIC_SYSTEM_TEST":
      value = process.env.EXPO_PUBLIC_SYSTEM_TEST
      break
    case "EXPO_PUBLIC_SYSTEM_TEST_CLIENT_WS_PORT":
      value = process.env.EXPO_PUBLIC_SYSTEM_TEST_CLIENT_WS_PORT
      break
    case "EXPO_PUBLIC_SYSTEM_TEST_HOST":
      value = process.env.EXPO_PUBLIC_SYSTEM_TEST_HOST
      break
    case "EXPO_PUBLIC_SYSTEM_TEST_PROJECT_SYNC":
      value = process.env.EXPO_PUBLIC_SYSTEM_TEST_PROJECT_SYNC
      break
    case "EXPO_PUBLIC_SYSTEM_TEST_QUERY_LOGGING":
      value = process.env.EXPO_PUBLIC_SYSTEM_TEST_QUERY_LOGGING
      break
    case "EXPO_PUBLIC_SYSTEM_TEST_SCOUNDREL_PORT":
      value = process.env.EXPO_PUBLIC_SYSTEM_TEST_SCOUNDREL_PORT
      break
    case "EXPO_PUBLIC_VELOCIOUS_ENV":
      value = process.env.EXPO_PUBLIC_VELOCIOUS_ENV
      break
    default:
      value = undefined
  }

  return typeof value === "string" && value ? value : undefined
}

/**
 * Resolves an Expo public env value in web and native builds.
 * @param {ExpoPublicEnvName} name Runtime environment variable identifier.
 * @returns {string | undefined} Configured value.
 */
export function expoPublicEnv(name) {
  const processValue = processEnvValue(name)

  if (processValue) return processValue

  const extraValue = expoPublicEnvFromConstants()[name]

  return typeof extraValue === "string" && extraValue ? extraValue : undefined
}

/**
 * Resolves an Expo public boolean flag.
 * @param {ExpoPublicEnvName} name Runtime environment variable identifier expected to contain "true".
 * @returns {boolean} Whether the flag is set to "true".
 */
export function expoPublicEnvFlag(name) {
  return expoPublicEnv(name) === "true"
}
